import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../services/api";

export interface PublicInvoiceDetails {
  invoice: {
    id: string;
    number?: string;
    status: "draft" | "open" | "paid" | "uncollectible" | "void";
    currency: string;
    amountDue: number;
    total: number;
    subtotal: number;
    dueDate?: string;
    description?: string;
    invoicePdf?: string;
  };
  customer: {
    name?: string;
    email?: string;
  };
  business: {
    name?: string;
    /** "workspace" when a team workspace sells, "coach" when a coach sells on his own. */
    kind?: "workspace" | "coach";
    /** The business mark (workspace logo, else the payment account's icon); drawn square. */
    icon?: string;
    /** The coach's own photo; drawn round. Never sent for a team. */
    photo?: string;
    logo?: string;
    supportEmail?: string;
    brandColor?: string;
    secondaryColor?: string;
  };
  lineItems: Array<{
    description?: string;
    quantity: number;
    unitAmount: number;
    amount: number;
  }>;
  subscription?: {
    productName: string;
    interval: string;
    intervalCount: number;
  };
  product?: {
    name: string;
    description?: string;
    avatar?: { type: string; value: string };
    branding?: { coverImageUrl?: string };
    highlights?: {
      included: Array<{ text: string }>;
      notIncluded: Array<{ text: string }>;
    };
  };
  stripeAccountId: string;
}

export interface PaymentIntentResponse {
  clientSecret: string;
  amount: number;
  currency: string;
  invoiceId: string;
}

export function useInvoiceDetails(invoiceId: string | undefined) {
  return useQuery({
    queryKey: invoiceKeys.details(invoiceId!),
    queryFn: async () => {
      const response = await api.get<PublicInvoiceDetails>(
        `/invoices/${invoiceId}/public`,
      );
      return response.data;
    },
    enabled: !!invoiceId,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      // Don't retry on 404 (not found) or 400 (bad request) — these won't resolve on retry
      const axiosError = error as { response?: { status?: number } };
      const status = axiosError?.response?.status;
      if (status === 404 || status === 400) return false;
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
}

// POST that creates a Stripe PaymentIntent — modeled as a mutation so it
// fires exactly once (on demand) and never re-fires on window refocus /
// remount / reconnect, which previously created duplicate PaymentIntents.
export function useCreatePaymentIntent() {
  return useMutation({
    mutationFn: async ({
      invoiceId,
      email,
    }: {
      invoiceId: string;
      email: string;
    }) => {
      const response = await api.post<PaymentIntentResponse>(
        `/invoices/${invoiceId}/pay`,
        { email },
      );
      return response.data;
    },
  });
}

export const invoiceKeys = {
  details: (id: string) => ["invoice", id] as const,
};
